import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  makeStyles,
} from '@material-ui/core';

/**
 * Mirrors the `delete_scope` parameter on the module templates' delete branch.
 * Sent verbatim, so these strings must match the template enum.
 */
export type DeleteScope = 'application' | 'application-and-database';

interface DeleteDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * `scope` is undefined for types whose database cannot be kept, in which case
   * the caller must not send `delete_scope` at all -- the template does not
   * declare it.
   */
  onConfirm: (scope?: DeleteScope) => void;
  applicationName: string;
  isExecuting: boolean;
  /**
   * Whether the LIVE Template declares `delete_scope`, i.e. whether the database
   * can actually be kept. 'unknown' while it is being read -- the confirm button
   * is disabled until it resolves, because offering "keep my database" against a
   * Template that cannot honour it destroys the data.
   */
  scopeSupport: 'unknown' | 'yes' | 'no';
}

const useStyles = makeStyles(theme => ({
  warning: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderLeft: `3px solid ${theme.palette.error.main}`,
    backgroundColor: theme.palette.background.default,
  },
  optionHint: {
    display: 'block',
    color: theme.palette.text.secondary,
  },
}));

export const DeleteDialog = ({
  open,
  onClose,
  onConfirm,
  applicationName,
  isExecuting,
  scopeSupport,
}: DeleteDialogProps) => {
  const classes = useStyles();
  const [scope, setScope] = React.useState<DeleteScope>('application');

  // Always reopen on the safe option, so a previous destructive choice is never
  // inherited by the next deletion.
  React.useEffect(() => {
    if (open) {
      setScope('application');
    }
  }, [open]);

  const separable = scopeSupport === 'yes';
  const wipesDatabase = separable && scope === 'application-and-database';

  const handleClose = () => {
    if (!isExecuting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="delete-dialog-title">Delete "{applicationName}"</DialogTitle>
      <DialogContent>
        {separable ? (
          <>
            <DialogContentText id="delete-dialog-description">
              Your GitHub repository is not affected either way, and you can deploy
              again with the same name afterwards.
            </DialogContentText>
            <FormControl component="fieldset" disabled={isExecuting}>
              <RadioGroup
                value={scope}
                onChange={e => setScope(e.target.value as DeleteScope)}
              >
                <FormControlLabel
                  value="application"
                  control={<Radio color="primary" />}
                  label={
                    <>
                      Delete the application, keep my database (recommended)
                      <Typography variant="caption" className={classes.optionHint}>
                        Removes the app, its address and its credentials. Your data
                        stays, and a new deployment picks it up.
                      </Typography>
                    </>
                  }
                />
                <FormControlLabel
                  value="application-and-database"
                  control={<Radio color="secondary" />}
                  label={
                    <>
                      Delete the application and wipe the database
                      <Typography variant="caption" className={classes.optionHint}>
                        Also removes the database and its storage.
                      </Typography>
                    </>
                  }
                />
              </RadioGroup>
            </FormControl>
            {wipesDatabase && (
              <div className={classes.warning}>
                <Typography variant="body2">
                  <strong>This permanently deletes your data.</strong> Every table,
                  row and migration in this app's database is erased. There is no
                  backup and this cannot be undone.
                </Typography>
              </div>
            )}
          </>
        ) : (
          <DialogContentText id="delete-dialog-description">
            {scopeSupport === 'unknown'
              ? 'Checking what this will delete...'
              : `This permanently removes "${applicationName}" and its stored data,
                 along with its entry in the catalog. There is no backup and this
                 cannot be undone. Your GitHub repository is not affected.`}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" disabled={isExecuting}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(separable ? scope : undefined)}
          color="secondary"
          variant="contained"
          disabled={isExecuting || scopeSupport === 'unknown'}
        >
          {isExecuting
            ? 'Deleting...'
            : wipesDatabase
            ? 'Delete and wipe database'
            : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
